import { useMemo, useState } from "react";
import {
  DndContext,
  type DragEndEvent,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortableSensors } from "@/lib/dnd-sensors";
import {
  JOURNEY_TASK_ACTION_KIND_LABEL,
  type JourneyTaskRow,
} from "@colab/shared";
import { ListChecks, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/empty-state";
import { formatApiError } from "@/lib/format-api-error";
import { useAddTask } from "./use-add-task";
import { useReorderTasks } from "./use-reorder-tasks";
import { TaskRow } from "./task-row";
import { TaskConfigSheet } from "./task-config-sheet";

// Phase 7 sub-bucket 14 — Tasks tab inside the journey builder.
//
// Single flat ordered task list rendered with @dnd-kit/sortable. Pointer
// sensor with 6px activation matches the sub-bucket 12 tier matrix convention.
// Reorder is optimistic via useReorderTasks (single PATCH of the full ordered
// id list — Phase 6 bucket 7 pattern).
//
// Task creation flows through useAddTask (pessimistic — server-generated id);
// the just-created task auto-opens in the config sheet so admins can configure
// kind + fields without an extra click.

export function JourneyTasksTab({
  journeyId,
  tasks,
}: {
  journeyId: string;
  tasks: JourneyTaskRow[];
}) {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const addTask = useAddTask();
  const reorderTasks = useReorderTasks();

  const orderedTasks = useMemo(
    () => [...tasks].sort((a, b) => a.position - b.position),
    [tasks],
  );
  const taskIds = useMemo(() => orderedTasks.map((t) => t.id), [orderedTasks]);

  const sensors = useSortableSensors();

  function onDragEnd(e: DragEndEvent) {
    const activeId = e.active.id as string;
    const overId = e.over?.id as string | undefined;
    if (!overId || activeId === overId) return;
    const fromIdx = taskIds.indexOf(activeId);
    const toIdx = taskIds.indexOf(overId);
    if (fromIdx < 0 || toIdx < 0) return;
    const orderedIds = arrayMove(taskIds, fromIdx, toIdx);
    reorderTasks.mutate({ journeyId, taskIds: orderedIds });
  }

  function handleAddTask() {
    addTask.mutate(
      {
        journeyId,
        input: {
          title: "Untitled task",
          description: null,
          assignee_kind: "partner",
          default_assignee_user_id: null,
          visibility: "public",
          due_offset_days: 0,
          action: { kind: "none" },
        },
      },
      {
        onSuccess: (res) => {
          setSelectedTaskId(res.id);
        },
        onError: (err) => toast.error(formatApiError(err, "Failed to add task")),
      },
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold tracking-tight">
          Tasks
        </h2>
        <Button
          type="button"
          size="sm"
          onClick={handleAddTask}
          disabled={addTask.isPending}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add task
        </Button>
      </div>

      {orderedTasks.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="No tasks yet"
          description="Add a task to start building this journey. Tasks render in order for partners after the journey is assigned."
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
            <ul className="space-y-2">
              {orderedTasks.map((task) => (
                <TaskRow
                  key={task.id}
                  journeyId={journeyId}
                  task={task}
                  canDrag={!reorderTasks.isPending}
                  actionKindLabel={JOURNEY_TASK_ACTION_KIND_LABEL[task.action.kind]}
                  onConfigure={() => setSelectedTaskId(task.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {selectedTaskId && (
        <TaskConfigSheet
          journeyId={journeyId}
          taskId={selectedTaskId}
          tasks={orderedTasks}
          onClose={() => setSelectedTaskId(null)}
        />
      )}
    </div>
  );
}
